import { useState, useEffect } from 'react';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import Icon from '../components/Icon';
import SchoolPicker from '../components/SchoolPicker';
import '../styles/StaffPages.css';
import apiService from '../services/api';

const normalizeIncentiveType = (type) => type === 'raffle_ticket' ? 'raffle_entry' : type;

const getIncentiveTypeLabel = (type) => normalizeIncentiveType(type);

const getIncentiveValueLabel = (campaign) => {
  const type = normalizeIncentiveType(campaign.incentive_type);
  if (type === 'points') return `${campaign.incentive_value} pts`;
  if (type === 'raffle_entry') return `${campaign.incentive_value} entries`;
  return `${campaign.incentive_value} ${type}`;
};

const CampaignFormModal = ({
  isOpen,
  isEditing,
  formData,
  setFormData,
  onSubmit,
  onClose,
  triggers,
  schools,
  raffles,
  modalOverlayStyle,
  modalContentStyle
}) => {
  const [choseAll, setChoseAll] = useState(false);

  // Seed All-schools only when the modal opens, not when the picker clears school.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- formData.school is the open-time seed
  useEffect(() => {
    if (!isOpen) return undefined;
    setChoseAll(Boolean(isEditing && !formData.school));
    return undefined;
  }, [isOpen, isEditing]);

  if (!isOpen) return null;

  const selectedSchool = schools.find(s => s.id === formData.school) || null;
  const isRaffleEntry = formData.incentive_type === 'raffle_entry';
  const isSpecificScope = formData.raffle_scope === 'specific';

  const toggleRaffle = (raffleId) => {
    const current = formData.raffles || [];
    const updated = current.includes(raffleId)
      ? current.filter(id => id !== raffleId)
      : [...current, raffleId];
    setFormData({ ...formData, raffles: updated });
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 className="staff-page-header" style={{ margin: 0 }}>
            {isEditing ? 'Edit Campaign' : 'Add New Campaign'}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
          >
            ×
          </button>
        </div>
        <form onSubmit={onSubmit} className="reward-form">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Campaign Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Trigger</label>
              <select
                value={formData.trigger}
                onChange={(e) => setFormData({ ...formData, trigger: e.target.value })}
                className="form-select"
                required
              >
                <option value="">Select Trigger</option>
                {triggers.map(trigger => (
                  <option key={trigger.id} value={trigger.id}>{trigger.name}</option>
                ))}
              </select>
            </div>
          </div>

            <div className="form-row">
            <div className="form-group">
              <label className="form-label">School (optional)</label>
              <SchoolPicker
                schools={schools}
                value={selectedSchool}
                onChange={(school) => {
                  setChoseAll(!school);
                  setFormData({ ...formData, school: school?.id || '', raffles: [] });
                }}
                allOptionLabel="All schools"
                allSelected={choseAll}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Incentive Type</label>
              <select
                value={formData.incentive_type}
                onChange={(e) => setFormData({ ...formData, incentive_type: e.target.value })}
                className="form-select"
              >
                <option value="points">Points</option>
                <option value="raffle_entry">Raffle Entry</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Incentive Value</label>
              <input
                type="number"
                value={formData.incentive_value}
                onChange={(e) => setFormData({ ...formData, incentive_value: e.target.value })}
                className="form-input"
                min="1"
                required
              />
            </div>
          </div>

          {isRaffleEntry && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Raffle Scope</label>
                  <select
                    value={formData.raffle_scope}
                    onChange={(e) => setFormData({ ...formData, raffle_scope: e.target.value, raffles: [] })}
                    className="form-select"
                  >
                    <option value="specific">Specific Raffle(s)</option>
                    <option value="all_school">All Active School Raffles</option>
                  </select>
                </div>
              </div>

              {isSpecificScope && (
                <div className="form-group">
                  <label className="form-label">Select Raffles</label>
                  {(() => {
                    const pickerRaffles = raffles.filter(r => !r.school_id || r.school_id === formData.school);
                    if (pickerRaffles.length === 0) {
                      return (
                        <p style={{ fontSize: '13px', color: formData.school ? '#999' : '#e17055', background: formData.school ? 'transparent' : '#fff5f5', border: formData.school ? 'none' : '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', margin: 0 }}>
                          {formData.school ? 'No raffles found for the selected school' : 'Please select a school'}
                        </p>
                      );
                    }
                    return (
                      <div style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        padding: '8px'
                      }}>
                        {pickerRaffles.map(raffle => {
                          const checked = (formData.raffles || []).includes(raffle.id);
                          return (
                            <label
                              key={raffle.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: checked ? '#f0f7ff' : 'transparent',
                                marginBottom: '2px'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleRaffle(raffle.id)}
                                style={{ width: '15px', height: '15px', cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '14px', fontFamily: '"Baikal Book"' }}>
                                {raffle.title}
                              </span>
                              {!raffle.school_id && (
                                <span style={{ fontSize: '11px', color: '#6b7280' }}>All schools</span>
                              )}
                              <span style={{
                                fontSize: '11px',
                                padding: '2px 6px',
                                borderRadius: '10px',
                                marginLeft: 'auto',
                                background: raffle.status === 'active' ? '#d1fae5' : '#fee2e2',
                                color: raffle.status === 'active' ? '#065f46' : '#991b1b'
                              }}>
                                {raffle.status}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}

              {formData.raffle_scope === 'all_school' && (
                <p style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  margin: '0 0 12px'
                }}>
                  User will be automatically entered into every active raffle for their school.
                  No specific raffle selection needed.
                </p>
              )}
            </>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Per User Limit</label>
              <input
                type="number"
                value={formData.per_user_limit}
                onChange={(e) => setFormData({ ...formData, per_user_limit: e.target.value })}
                className="form-input"
                min="1"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Total Limit (optional)</label>
              <input
                type="number"
                value={formData.total_limit}
                onChange={(e) => setFormData({ ...formData, total_limit: e.target.value })}
                className="form-input"
                min="1"
                placeholder="Leave empty for unlimited"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">End Date (optional)</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 0'
            }}>
              <input
                type="checkbox"
                id={isEditing ? "edit_is_active" : "is_active"}
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer'
                }}
              />
              <label
                htmlFor={isEditing ? "edit_is_active" : "is_active"}
                className="form-label"
                style={{
                  margin: 0,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Active Campaign
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="staff-page-button submit-button">
              <span className="staff-page-button-text">
                {isEditing ? 'Update Campaign' : 'Create Campaign'}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SuperuserIncentiveManagement = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [openMenuCampaignId, setOpenMenuCampaignId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    incentive_type: 'points',
    incentive_value: '',
    raffle_scope: 'specific',
    raffles: [],
    trigger: '',
    school: '',
    total_limit: '',
    per_user_limit: '1',
    start_date: '',
    end_date: '',
    is_active: true,
    incentive_metadata: {}
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuCampaignId && !event.target.closest('[data-campaign-menu]')) {
        setOpenMenuCampaignId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuCampaignId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [campaignsResponse, triggersResponse, schoolsResponse, rafflesResponse] = await Promise.all([
        apiService.getIncentiveCampaigns(),
        apiService.getIncentiveTriggers(),
        apiService.getPublicSchools(),
        apiService.getRaffles(),
      ]);

      setCampaigns(campaignsResponse.results || []);
      setTriggers(triggersResponse.results || []);
      setSchools(schoolsResponse.results || []);
      setRaffles(rafflesResponse.results || []);

    } catch (error) {
      console.error('Failed to fetch incentive data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanFormData = {
        ...formData,
        school: formData.school || null,
        total_limit: formData.total_limit ? parseInt(formData.total_limit) : null,
        incentive_value: parseInt(formData.incentive_value),
        per_user_limit: parseInt(formData.per_user_limit),
        start_date: formData.start_date ? `${formData.start_date}T00:00:00Z` : null,
        end_date: formData.end_date ? `${formData.end_date}T23:59:59Z` : null,
        raffle_scope: formData.incentive_type === 'raffle_entry' ? (formData.raffle_scope || 'specific') : 'specific',
        raffles: formData.incentive_type === 'raffle_entry' && formData.raffle_scope === 'specific'
          ? (formData.raffles || [])
          : [],
      };

      if (editingCampaign) {
        // Update existing campaign
        await apiService.updateIncentiveCampaign(editingCampaign.id, cleanFormData);
      } else {
        // Create new campaign
        await apiService.createIncentiveCampaign(cleanFormData);
      }

      setShowAddForm(false);
      setShowEditForm(false);
      setEditingCampaign(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save campaign:', error?.response?.data || error);
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      incentive_type: normalizeIncentiveType(campaign.incentive_type),
      incentive_value: campaign.incentive_value.toString(),
      raffle_scope: campaign.raffle_scope || 'specific',
      raffles: (campaign.raffles || []).map(r => (typeof r === 'object' ? r.id : r)),
      trigger: campaign.trigger,
      school: campaign.school || '',
      total_limit: campaign.total_limit?.toString() || '',
      per_user_limit: campaign.per_user_limit.toString(),
      start_date: campaign.start_date?.split('T')[0] || '',
      end_date: campaign.end_date?.split('T')[0] || '',
      is_active: campaign.is_active,
      incentive_metadata: campaign.incentive_metadata || {}
    });
    setShowEditForm(true);
  };

  const handleDelete = async (campaignId) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        await apiService.deleteIncentiveCampaign(campaignId);
        fetchData();
      } catch (error) {
        console.error('Failed to delete campaign:', error);
      }
    }
  };

  const handleMenuToggle = (campaignId) => {
    setOpenMenuCampaignId(openMenuCampaignId === campaignId ? null : campaignId);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      incentive_type: 'points',
      incentive_value: '',
      raffle_scope: 'specific',
      raffles: [],
      trigger: '',
      school: '',
      total_limit: '',
      per_user_limit: '1',
      start_date: '',
      end_date: '',
      is_active: true,
      incentive_metadata: {}
    });
  };

  const handleAddClose = () => {
    setShowAddForm(false);
    resetForm();
  };

  const handleEditClose = () => {
    setShowEditForm(false);
    setEditingCampaign(null);
    resetForm();
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedCampaigns = () => {
    let filtered = campaigns;

    // Filter by search term
    if (searchTerm) {
      filtered = campaigns.filter(campaign =>
        campaign.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'incentive_value') {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  };


  const getTriggerName = (triggerId) => {
    const trigger = triggers.find(t => t.id === triggerId);
    return trigger ? trigger.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />

          <div className="staff-page-header-row">
            <div>
              <h1 className="staff-page-header">Manage Incentives</h1>
              <p className="staff-page-subheader">Manage action-based reward campaigns</p>
            </div>
            <button
              className="staff-page-button"
              onClick={() => setShowAddForm(true)}
            >
              <span className="staff-page-button-text">Add Campaign</span>
            </button>
          </div>

          {/* Search Bar */}
          <div style={{ marginTop: '32px', marginBottom: '24px' }}>
            <input
              type="text"
              placeholder="Search campaigns . . ."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '50%',
                padding: '12px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: '"Baikal Book"'
              }}
            />
          </div>

          {/* Campaigns Table */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '1270px',
            marginTop: '24px'
          }}>
            {filteredAndSortedCampaigns().length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#666',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e9ecef'
              }}>
                {searchTerm ? 'No campaigns match your search' : 'No campaigns created yet'}
              </div>
            ) : (
              <>
                {/* Header */}
                <div style={{
                  display: 'flex',
                  width: '100%',
                  height: '45px',
                  padding: '0 18px',
                  alignItems: 'center',
                  borderRadius: '12px 12px 0 0',
                  background: '#F8F8F8',
                  backdropFilter: 'blur(22.5px)'
                }}>
                  <div
                    onClick={() => handleSort('name')}
                    style={{
                      fontFamily: '"Baikal Book"',
                      fontWeight: '900',
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      width: '23%',
                      minWidth: '0',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: 'pointer',
                      paddingRight: '12px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                    Campaign Name
                    <Icon name="up-down" size={12} />
                  </div>
                  <div
                    onClick={() => handleSort('incentive_type')}
                    style={{
                      fontFamily: '"Baikal Book"',
                      fontWeight: '900',
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      width: '14%',
                      minWidth: '0',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: 'pointer',
                      paddingRight: '12px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                    Type
                    <Icon name="up-down" size={12} />
                  </div>
                  <div
                    onClick={() => handleSort('incentive_value')}
                    style={{
                      fontFamily: '"Baikal Book"',
                      fontWeight: '900',
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      width: '11%',
                      minWidth: '0',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: 'pointer',
                      paddingRight: '12px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                    Value
                    <Icon name="up-down" size={12} />
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontWeight: '900',
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    width: '15%',
                    minWidth: '0',
                    flexShrink: 0,
                    paddingRight: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    Trigger
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontWeight: '900',
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    width: '15%',
                    minWidth: '0',
                    flexShrink: 0,
                    paddingRight: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    School
                  </div>
                  <div
                    onClick={() => handleSort('start_date')}
                    style={{
                      fontFamily: '"Baikal Book"',
                      fontWeight: '900',
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      width: '10%',
                      minWidth: '0',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: 'pointer',
                      paddingRight: '12px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                    Start
                    <Icon name="up-down" size={12} />
                  </div>
                  <div
                    onClick={() => handleSort('end_date')}
                    style={{
                      fontFamily: '"Baikal Book"',
                      fontWeight: '900',
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      width: '10%',
                      minWidth: '0',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: 'pointer',
                      paddingRight: '12px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                    End
                    <Icon name="up-down" size={12} />
                  </div>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontWeight: '900',
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    width: '6%',
                    minWidth: '40px',
                    flexShrink: 0,
                    textAlign: 'center'
                  }}>
                    {/* Actions column header */}
                  </div>
                </div>

                <div style={{ marginBottom: '8px' }}></div>

                {/* Table Rows */}
                {filteredAndSortedCampaigns().map((campaign) => {
                  // Check if campaign is expired
                  const now = new Date();
                  const endDate = campaign.end_date ? new Date(campaign.end_date) : null;
                  const startDate = campaign.start_date ? new Date(campaign.start_date) : null;
                  const isExpired = endDate && endDate < now;
                  const notStarted = startDate && startDate > now;
                  const isOutsideDateRange = isExpired || notStarted;

                  return (
                  <div key={campaign.id} style={{
                    display: 'flex',
                    width: '100%',
                    height: '59px',
                    padding: '18px',
                    alignItems: 'center',
                    borderRadius: '12px',
                    background: '#F8F8F8',
                    marginBottom: '8px',
                    opacity: isOutsideDateRange ? 0.5 : 1
                  }}>
                    <div style={{
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      width: '23%',
                      minWidth: '0',
                      flexShrink: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: '59px',
                      paddingRight: '12px'
                    }}>
                      {campaign.name}
                    </div>
                    <div style={{
                      width: '14%',
                      minWidth: '0',
                      flexShrink: 0,
                      paddingRight: '12px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: normalizeIncentiveType(campaign.incentive_type) === 'points' ? '#e3f2fd' : '#f3e5f5',
                        color: normalizeIncentiveType(campaign.incentive_type) === 'points' ? '#1976d2' : '#7b1fa2',
                        whiteSpace: 'nowrap'
                      }}>
                        {getIncentiveTypeLabel(campaign.incentive_type)}
                      </span>
                    </div>
                    <div style={{
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      width: '11%',
                      minWidth: '0',
                      flexShrink: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: '59px',
                      paddingRight: '12px'
                    }}>
                      {getIncentiveValueLabel(campaign)}
                    </div>
                    <div style={{
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      width: '15%',
                      minWidth: '0',
                      flexShrink: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: '59px',
                      paddingRight: '12px',
                      opacity: 0.7
                    }}>
                      {getTriggerName(campaign.trigger)}
                    </div>
                    <div style={{
                      width: '15%',
                      minWidth: '0',
                      flexShrink: 0,
                      paddingRight: '12px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      height: '59px'
                    }}>
                      {campaign.school ? (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {schools.find(s => s.id === campaign.school)?.image && (
                            <img
                              src={schools.find(s => s.id === campaign.school)?.image}
                              alt="School logo"
                              style={{
                                width: '32px',
                                height: '32px',
                                marginRight: '8px',
                                objectFit: 'contain'
                              }}
                            />
                          )}
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#666'
                          }}>
                            {schools.find(s => s.id === campaign.school)?.name || 'Unknown School'}
                          </span>
                        </div>
                      ) : (
                        <span style={{
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#999',
                          fontStyle: 'italic'
                        }}>
                          All Schools
                        </span>
                      )}
                    </div>
                    <div style={{
                      width: '10%',
                      minWidth: '0',
                      flexShrink: 0,
                      paddingRight: '12px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: isOutsideDateRange ? '#999' : '#666'
                    }}>
                      {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                    </div>
                    <div style={{
                      width: '10%',
                      minWidth: '0',
                      flexShrink: 0,
                      paddingRight: '12px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: isExpired ? '#e17055' : (isOutsideDateRange ? '#999' : '#666')
                    }}>
                      {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'No end'}
                    </div>
                    <div style={{
                      width: '6%',
                      minWidth: '40px',
                      flexShrink: 0,
                      textAlign: 'center',
                      position: 'relative'
                    }} data-campaign-menu>
                      <button
                        onClick={() => handleMenuToggle(campaign.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '12px',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#374151',
                          fontSize: '20px',
                          fontWeight: 'bold'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#f3f4f6';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        <Icon name="more-vertical" size={24} style={{ color: '#374151' }} />
                        ⋮
                      </button>
                      {openMenuCampaignId === campaign.id && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: '0',
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          zIndex: 1000,
                          minWidth: '140px'
                        }}>
                          <button
                            onClick={() => {
                              handleEdit(campaign);
                              setOpenMenuCampaignId(null);
                            }}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontFamily: '"Baikal Book"',
                              borderRadius: '8px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#f3f4f6';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              handleDelete(campaign.id);
                              setOpenMenuCampaignId(null);
                            }}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontFamily: '"Baikal Book"',
                              color: '#dc3545',
                              borderRadius: '8px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#f3f4f6';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Add Campaign Modal */}
          <CampaignFormModal
            isOpen={showAddForm}
            isEditing={false}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onClose={handleAddClose}
            triggers={triggers}
            schools={schools}
            raffles={raffles}
            modalOverlayStyle={modalOverlayStyle}
            modalContentStyle={modalContentStyle}
          />

          {/* Edit Campaign Modal */}
          <CampaignFormModal
            isOpen={showEditForm}
            isEditing={true}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onClose={handleEditClose}
            triggers={triggers}
            schools={schools}
            raffles={raffles}
            modalOverlayStyle={modalOverlayStyle}
            modalContentStyle={modalContentStyle}
          />
        </div>
      </div>
    </div>
  );
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  background: 'white',
  borderRadius: '12px',
  padding: '32px',
  width: '90%',
  maxWidth: '800px',
  maxHeight: '90vh',
  overflow: 'auto'
};

export default SuperuserIncentiveManagement;